<template>
  <div :id="id" class="chart">
    <div :id="`horizontalBarScrollContainer_${id}`" class="scroll-container">
      <svg :id="`svg_${id}`"></svg>
    </div>
    <div :id="`horizontalBarChartTooltipDiv_${id}`" class="tooltip">
      <svg :id="`horizontalBarChartTooltip_${id}`" height="100%" width="100%"></svg>
    </div>
    <!-- <svg :id="`xAxisSvg_${id}`"></svg> -->
  </div>
</template>

<script>
import * as d3 from 'd3'
import Colors from '@/utils/colors'

export default {
  name: 'HorizontalStackedBarChart',
  props: {
    dataset: {
      type: Object,
      default() {
        return {}
      }
    },
    id: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      tooltipPosition: { top: 0, right: 170 },
      xAxisSvgHeight: 20,
      width: 0,
      height: 0,
      margins: 0,
      resizeListener: null,
      dominantBaseline: 'text-bottom',
      textOffset: 10
    }
  },
  computed: {
    svgHeight() {
      if (!this.dataset.values.length) return 0
      return this.dataset.values.length * 40 + 100
    },
    ctrWidth() {
      return this.width - 15
    },
    ctrHeight() {
      return this.svgHeight - 10 * 2
    },
    stackGenerator() {
      const columns = this.dataset.values.length ? this.dataset.columns : []
      return d3.stack().keys(columns)
    },
    stackData() {
      return this.stackGenerator(this.dataset.values).map((dataGroup) => {
        dataGroup.forEach((state) => {
          state.key = dataGroup.key
        })
        return dataGroup
      })
    },
    xScale() {
      return d3
        .scaleLinear()
        .domain([
          0,
          d3.max(this.stackData, (dataGroup) => {
            return d3.max(dataGroup, (state) => state[1])
          })
        ])
        .range([0, this.ctrWidth])
    },
    yScale() {
      return d3
        .scaleBand()
        .domain(this.dataset.values.map((d) => this.xAccessor(d)))
        .range([0, this.ctrHeight])
        .padding(0.1)
    },
    yAxis() {
      return d3.axisLeft(this.yScale)
    },
    xAxis() {
      return d3
        .axisBottom(this.xScale)
        .tickFormat((d) => d + '%')
        .tickValues([0, 50, 100])
    },
    shouldPaintHatch() {
      if (!this.dataset.hatched) return false
      return Object.entries(this.dataset.hatched).find((el) => {
        if (el[1]) return el
      })
    }
  },
  watch: {
    dataset() {
      this.draw()
    }
  },
  mounted() {
    this.init()
    this.resizeListener = () => {
      this.draw()
    }
    window.addEventListener('resize', this.resizeListener)
  },
  unmounted() {
    window.removeEventListener('resize', this.resizeListener)
  },
  methods: {
    addHatch(ctr) {
      ctr.append('g').attr('id', `horizontalBarGroupHatch_${this.id}`)
      ctr
        .append('g')
        .append('defs')
        .append('pattern')
        .attr('id', 'diagonalHatch')
        .attr('patternUnits', 'userSpaceOnUse')
        .attr('width', 6)
        .attr('height', 6)
        .append('path')
        .attr('d', 'M-1.5,1.5 l3,-3 M0,6 l6,-6 M5,7 l3,-3')
        .attr('stroke', Colors.theme.purple)
        .attr('stroke-width', 2)
    },
    xAccessor(d) {
      return d.room
    },
    yAccessor(d) {
      return parseInt(d.y)
    },
    drawAxis() {
      if (!this.dataset.values.length) return
      d3.select(`#horizontalBarAxisGroupX_${this.id}`)
        .style('position', 'fixed')
        .call(this.xAxis)
        .call((g) => g.select('.domain').remove())
        .call((g) => g.selectAll('line').remove())
        .selectAll('.tick')
        .selectAll('text')
        .attr('fill', Colors.theme.darkGray)
        .style('font-family', 'Inter')
        .style('font-size', '10px')
        .style('line-height', '12px')
    },
    drawBars() {
      const that = this
      const barGroups = d3
        .select(`#horizontalBarGroup_${this.id}`)
        .selectAll('g')
        .data(this.stackData)
        .join('g')
      barGroups
        .selectAll('rect')
        .data((d) => d)
        .join('rect')
        .attr('x', (d) => this.xScale(d[0]))
        .attr('y', (d) => this.yScale(d.data.room) + this.yScale.bandwidth() / 5)
        .attr('width', (d) => this.xScale(d[1]) - this.xScale(d[0]))
        .attr('height', this.yScale.bandwidth() / 4)
        .attr('fill', (d) => {
          if (that.shouldPaintHatch) {
            return this.dataset.hatched[d.key] ? this.dataset.colors[d.key] : 'transparent'
          }
          return this.dataset.colors[d.key]
        })
        .attr('rx', 1)
        .attr('ry', 1)
      if (this.shouldPaintHatch) {
        this.drawHatch()
        return
      }
      barGroups
        .selectAll('rect')
        .on('mouseover', function (event, d) {
          that.onHoverChart(d, this)
        })
        .on('mouseout', function () {
          that.onHoverLeaveChart(this)
        })
    },
    drawHatch() {
      const that = this
      const barGroups = d3
        .select(`#horizontalBarGroupHatch_${this.id}`)
        .selectAll('g')
        .data(this.stackData)
        .join('g')
      barGroups
        .selectAll('rect')
        .data((d) => d)
        .join('rect')
        .attr('x', (d) => this.xScale(d[0]))
        .attr('y', (d) => this.yScale(d.data.room) + this.yScale.bandwidth() / 5)
        .attr('width', (d) => this.xScale(d[1]) - this.xScale(d[0]))
        .attr('height', this.yScale.bandwidth() / 4)
        .attr('fill', (d) =>
          this.dataset.hatched[d.key] ? 'url(#diagonalHatch)' : this.dataset.colors[d.key]
        )
        .attr('rx', 1)
        .attr('ry', 1)
        .on('mouseover', function (event, d) {
          that.onHoverChart(d, this)
        })
        .on('mouseout', function () {
          that.onHoverLeaveChart(this)
        })
    },
    onHoverChart(d, rectObj) {
      const name = this.dataset.names[d.key]
      const color = this.dataset.colors[d.key]
      const value = d.data[d.key]
      const tooltip = d3.select(`#horizontalBarChartTooltipDiv_${this.id}`)
      tooltip
        .style('display', 'block')
        .style('top', this.tooltipPosition.top + 'px')
        .style('left', this.ctrWidth - this.tooltipPosition.right + 'px')
        .style('width', '170px')
      const yLabel = d3.select(`#horizontalBarTooltipWindowYLabel_${this.id}`)
      const xLabel = d3.select(`#horizontalBarTooltipWindowXLabel_${this.id}`)
      yLabel.selectAll('circle').attr('fill', color)
      if (this.shouldPaintHatch) {
        const tooltipCircleHatch = d3.select(`#tooltip_circle_hatch_${this.id}`)
        tooltipCircleHatch.attr(
          'fill',
          this.dataset.hatched[d.key] ? `url(#hatch_pattern_circle_${this.id})` : 'transparent'
        )
      }

      yLabel.selectAll('text').text(`${name}: ${Math.round(value)}%`)
      xLabel.selectAll('text').text(d.data.room)
      d3.select(rectObj).style('opacity', 0.7)
    },
    onHoverLeaveChart(rectObj) {
      const tooltip = d3.select(`#horizontalBarChartTooltipDiv_${this.id}`)
      tooltip.style('display', 'none')
      d3.select(rectObj).style('opacity', 1)
    },
    drawBarLabels() {
      d3.select(`#horizontalBarLabels_${this.id}`)
        .selectAll('text')
        .data(this.dataset.values)
        .join((enter) => enter.append('text'))
        .attr('x', this.xScale(0))
        .attr('y', (d) => this.yScale(d.room) + this.yScale.bandwidth() / 5 - this.textOffset)
        .attr('dominant-baseline', this.dominantBaseline)
        .style('font-family', 'Inter')
        .style('font-size', '13px')
        .style('line-height', '16px')
        .style('font-weight', '500')
        .text((d) => d.room)
        .append('tspan')
        .attr('dx', '8px')
        .text((d) => d.roomAlias)
        .style('font-family', 'Inter')
        .style('font-size', '13px')
        .style('fill', '#767676')
        .style('font-weight', '500')
    },
    createTooltipWindow() {
      const tooltipWindow = d3.select(`#horizontalBarChartTooltip_${this.id}`)
      const yLabel = tooltipWindow
        .append('g')
        .attr('id', `horizontalBarTooltipWindowYLabel_${this.id}`)
      this.createTooltipDot(yLabel)
      yLabel
        .append('text')
        .attr('x', 20)
        .attr('y', 13)
        .attr('fill', Colors.theme.black)
        .style('font-size', '10px')
        .style('font-family', 'Inter')
        .style('line-height', '14px')
        .attr('fill', Colors.theme.gray)
      const xLabel = tooltipWindow
        .append('g')
        .attr('id', `horizontalBarTooltipWindowXLabel_${this.id}`)
      xLabel
        .append('text')
        .attr('x', 10)
        .attr('y', 30)
        .attr('fill', Colors.theme.black)
        .style('font-size', '10px')
        .attr('fill', Colors.theme.gray)
    },
    createTooltipDot(yLabel) {
      yLabel
        .append('circle')
        .attr('cx', 10)
        .attr('cy', 10)
        .attr('r', 6)
        .attr('fill', Colors.theme.black)
      if (this.shouldPaintHatch) {
        yLabel
          .append('g')
          .append('defs')
          .append('pattern')
          .attr('id', `hatch_pattern_circle_${this.id}`)
          .attr('patternUnits', 'userSpaceOnUse')
          .attr('width', 6)
          .attr('height', 6)
          .append('path')
          .attr('d', 'M-1.5,1.5 l3,-3 M0,6 l6,-6 M5,7 l3,-3')
          .attr('stroke', Colors.theme.purple)
          .attr('stroke-width', 2)
        yLabel
          .append('circle')
          .attr('id', `tooltip_circle_hatch_${this.id}`)
          .attr('cx', 10)
          .attr('cy', 10)
          .attr('r', 6)
      }
    },
    drawSvg() {
      const e = d3.select(`#horizontalBarScrollContainer_${this.id}`)
      const currentWidth = parseInt(e.node() ? e.style('width') : 0, 10)
      this.width = currentWidth
      const xAxisSvg = d3.select(`#xAxisSvg_${this.id}`)
      const barSvg = d3.select(`#svg_${this.id}`)
      xAxisSvg.attr('width', currentWidth)
      xAxisSvg.attr('height', this.xAxisSvgHeight)
      barSvg.attr('width', currentWidth)
      barSvg.attr('height', this.svgHeight)
    },
    initSvg() {
      this.drawSvg()
      const barSvg = d3.select(`#svg_${this.id}`)
      const xAxisSvg = d3.select(`#xAxisSvg_${this.id}`)
      xAxisSvg
        .append('g')
        .attr('transform', `translate(${this.margins}, 0)`)
        .attr('id', `horizontalBarAxisGroupX_${this.id}`)
      const ctr = barSvg.append('g').attr('transform', `translate(${this.margins}, 10)`)
      ctr.append('g').attr('id', `horizontalBarLabels_${this.id}`)
      ctr.append('g').attr('id', `horizontalBarGroup_${this.id}`)
      ctr.append('g').attr('id', `horizontalBarAxisGroupY_${this.id}`)

      if (this.shouldPaintHatch) this.addHatch(ctr)
    },
    init() {
      this.initSvg()
      this.drawBars()
      this.drawBarLabels()
      this.drawAxis()
      this.createTooltipWindow()
    },
    draw() {
      this.drawSvg()
      this.drawBars()
      this.drawBarLabels()
      this.drawAxis()
    }
  }
}
</script>

<style scoped lang="scss">
@use 'src/design-system/design-tokens/mixins.scss';
.chart {
  height: 100%;
  width: 100%;
  position: relative;
}

.scroll-container {
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  @include mixins.scrollbar-vertical;
}

.tooltip {
  height: 50px;
  width: 170px;
  border: 1px solid #ccc;
  position: absolute;
  padding: 5px;
  background-color: #fff;
  display: none;
  pointer-events: none;
  border-radius: 10px;
}
</style>
